import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Clock, DollarSign, Zap, ListChecks, Loader2, CheckCircle2, Radio,
} from "lucide-react";
import api from "@/lib/api";
import { iconFor } from "@/lib/agentIcons";
import {
  CinematicStage, StageHeader, GlassCard, RoomBackdrop, Pill, StatCard,
  EmptyState, PipelineRail, cx,
} from "@/components/shared";
import { PRODUCTION_JOURNEY, ROOM_BACKDROPS } from "@/lib/studio";

export default function ProductionDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState({});
  const timer = useRef(null);

  useEffect(() => {
    api.get("/catalogs").then(({ data }) => setAgents(data.agents || {}))
      .catch((e) => console.error("[ProductionDashboard] failed to load catalogs/agents", e));
    let active = true;
    const poll = async () => {
      try { const { data } = await api.get("/production/live"); if (active) setData(data); }
      catch (e) { console.error("[ProductionDashboard] production/live poll failed", e); }
      if (active) timer.current = setTimeout(poll, 3000);
    };
    poll();
    return () => { active = false; clearTimeout(timer.current); };
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#5B4DFF]" /></div>;

  const a = data.active;
  const agentMeta = a?.active_agent
    ? Object.values(agents).find((x) => x.name === a.active_agent)
    : null;
  const ActiveIcon = agentMeta ? iconFor(agentMeta.icon) : Radio;

  const total = PRODUCTION_JOURNEY.length;
  const doneCount = a ? Math.floor((a.progress / 100) * total) : 0;
  const doneKeys = PRODUCTION_JOURNEY.slice(0, doneCount).map((s) => s.key);
  const activeKey = a ? PRODUCTION_JOURNEY[Math.min(doneCount, total - 1)].key : null;

  return (
    <CinematicStage>
      <StageHeader
        stage="LIVE"
        room="The Director's Control Room"
        icon={Radio}
        title="Your studio, in motion"
        subtitle="The production floor — watch your AI crew work every stage in real time."
        actions={<Pill tone={a ? "gold" : "green"}><Radio className={cx("w-3 h-3", a && "animate-pulse")} /> {a ? "Rolling" : "Idle"}</Pill>}
      />

      {a ? (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="live-production-panel">
          <div className="lg:col-span-2 space-y-6">
            <RoomBackdrop image={ROOM_BACKDROPS.control} className="letterbox p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300]">Now producing</div>
                  <button onClick={() => navigate(`/projects/${a.id}`)} className="font-display text-2xl font-bold hover:text-[#b6aaff] transition text-left">{a.title}</button>
                </div>
                <div className="font-display text-4xl font-bold text-gradient">{a.progress}%</div>
              </div>

              <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-6">
                <motion.div className="h-full rail-line" animate={{ width: `${a.progress}%` }} />
              </div>

              <PipelineRail doneKeys={doneKeys} activeKey={activeKey} compact />

              <AnimatePresence mode="wait">
                <motion.div key={a.active_agent} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="mt-6 flex items-center gap-4 rounded-2xl bg-black/30 border border-[#5B4DFF]/30 p-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center pulse-ring">
                    <ActiveIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">{a.active_agent || "AI Agent"}</div>
                    <div className="text-sm text-slate-400">{agentMeta?.role || "Working on your production…"}</div>
                  </div>
                  <Loader2 className="w-5 h-5 animate-spin text-[#FFB300]" />
                </motion.div>
              </AnimatePresence>
            </RoomBackdrop>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Completed" value={`${a.completed_steps}/${a.total_steps}`} icon={ListChecks} />
              <StatCard label="Elapsed ~" value={`${a.elapsed_estimate}s`} icon={Clock} />
              <StatCard label="Remaining ~" value={`${a.remaining_estimate}s`} icon={Clock} tone="gold" />
              <StatCard label="Cost so far" value={`$${a.cost_spent}`} icon={DollarSign} tone="gold" />
            </div>

            <GlassCard className="p-6">
              <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-[#FFB300]" /> Crew on the floor</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {(a.pipeline || []).map((s) => {
                  const m = Object.values(agents).find((x) => x.name === s.agent);
                  const Icon = m ? iconFor(m.icon) : Activity;
                  const running = s.status === "running";
                  const done = ["done", "skipped", "waiting_provider"].includes(s.status);
                  return (
                    <div key={s.id} className={cx("flex items-center gap-3 rounded-xl px-3 py-2 border",
                      running ? "bg-[#FFB300]/10 border-[#FFB300]/30" : done ? "bg-white/5 border-white/10" : "bg-transparent border-white/5")}>
                      <Icon className={cx("w-4 h-4", running ? "text-[#FFB300]" : done ? "text-emerald-400" : "text-slate-600")} />
                      <span className={cx("text-sm flex-1", done || running ? "text-white" : "text-slate-500")}>{s.agent}</span>
                      {running && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FFB300]" />}
                      {done && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          </div>

          <div className="space-y-6">
            <GlassCard className="p-6">
              <h3 className="font-display font-semibold mb-3">Call Sheet · Queue</h3>
              {data.queue.length === 0 ? <p className="text-sm text-slate-500">Nothing queued.</p> :
                data.queue.map((q) => <div key={q.id} className="text-sm text-slate-300 py-1.5 truncate">{q.title}</div>)}
            </GlassCard>
            <RecentActivity items={data.recent_activity} />
          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="production-idle">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatCard label="Total Films" value={data.totals.projects} icon={ListChecks} />
            <StatCard label="Final Cuts" value={data.totals.ready} icon={CheckCircle2} tone="gold" />
            <StatCard label="Studio Status" value="Idle" icon={Radio} />
          </div>
          <EmptyState
            image={ROOM_BACKDROPS.control}
            icon={Activity}
            title="The floor is quiet"
            subtitle="Start a new film and this control room comes alive with your AI production crew."
            action={<button onClick={() => navigate("/create")} className="px-5 py-2.5 rounded-xl bg-[#5B4DFF] text-white font-semibold">Create Something</button>}
          />
          <RecentActivity items={data.recent_activity} />
        </div>
      )}
    </CinematicStage>
  );
}

function RecentActivity({ items }) {
  const fmt = (t) => { try { return new Date(t).toLocaleTimeString(); } catch { return ""; } };
  return (
    <GlassCard className="p-6">
      <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-[#5B4DFF]" /> Recent Activity</h3>
      {(!items || items.length === 0) ? <p className="text-sm text-slate-500">No activity yet.</p> : (
        <div className="space-y-2">
          {items.map((l, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5B4DFF]" />
              <span className="text-slate-300 flex-1 truncate">{l.detail || l.event}</span>
              <span className="text-xs text-slate-600 font-mono">{fmt(l.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}
